import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { Capacitor } from '@capacitor/core';
import { updateCustomerEntitlements, updateCustomerPlatformPurchase } from 'src/app/auth/ngrx/customer.actions';
import { PurchasePlatform } from '../models/purchase_platform.enum';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Purchases as PurchasesJs } from "@revenuecat/purchases-js";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

/**
 * Service responsible for dynamically loading heavy modules to improve startup performance
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleLoaderService {

  constructor(private store: Store<any>) {}

  private isInitialized = signal(false);

  /**
   * Initialize Social Login after app has started
   */
  async setupSocialLogin() {
    if (Capacitor.getPlatform() !== "ios") {
      SocialLogin.initialize({
        google: {
          iOSClientId: environment.socialLogin.ios.google,
          webClientId: environment.socialLogin.android.google,
        },
      });
    } else if (Capacitor.getPlatform() === "ios") {
      SocialLogin.initialize({
        google: {
          iOSClientId: environment.socialLogin.ios.google,
          webClientId: environment.socialLogin.android.google,
        },
        apple: {},
      });
    }
  }
  
  /**
   * Initialize RevenueCat after app has started
   * This is deliberately separated from the main startup sequence
   */
  async setupRevenueCat() {
    if (Capacitor.getPlatform() === "web") {
      return;
    } else if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() === "ios") {
        await Purchases.configure({
          apiKey: environment.revenueCat.ios,
        });
      } else if (Capacitor.getPlatform() === "android") {
        await Purchases.configure({
          apiKey: environment.revenueCat.android
        });
      }
    }
  }

  async subscribeToRevenueCatUserData(userId: string) {
    if (!this.isInitialized()) {
      await this.setupRevenueCat();
      this.isInitialized.set(true);
    }
    if (userId) {
      if (Capacitor.getPlatform() === "web") {
        await PurchasesJs.configure(
          environment.revenueCat.web,
          userId
        );
        PurchasesJs.getSharedInstance().getCustomerInfo().then(customerInfo => {
          this.updateCustomerInfo(customerInfo);
        });
      } else {
        const result = await Purchases.logIn({
          appUserID: userId,
        });

        this.updateCustomerInfo(result.customerInfo);
      }
    }
    
  }
  
  /**
   * Notify CapacitorUpdater that app is ready (call early in startup)
   * This is needed for over-the-air updates
   */
  async notifyUpdaterReady() {
    if (Capacitor.isNativePlatform()) {
      try {
        CapacitorUpdater.notifyAppReady();
      } catch (error) {
        console.error('Error notifying updater ready:', error);
      }
    }
  }
  
  private updateCustomerInfo(customerInfo: any) {
    this.store.dispatch(
      updateCustomerEntitlements({
        entitlements: Array.from(customerInfo.activeSubscriptions),
      })
    );
    
    this.store.dispatch(
      updateCustomerPlatformPurchase({
        platformPurchase: customerInfo.managementURL?.includes("google")
          ? PurchasePlatform.google
          : customerInfo.managementURL?.includes("apple")
          ? PurchasePlatform.apple
          : PurchasePlatform.web,
      })
    );
  }
} 